import React from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const advantages = [
  { stat: '20% Revenue Boost', desc: 'Certified Billing' },
  { stat: '24/7 Support', desc: 'Dedicated Team' },
  { stat: 'Fewer Denials', desc: 'Proactive Auditing' },
  { stat: '100% Transparency', desc: 'Client Reporting' },
  { stat: 'HIPAA Compliant', desc: 'Secure Processes' },
  { stat: '90% Accuracy', desc: 'QA Clinicians' },
];

const transformItems = [
  { title: 'Precision Recovery', desc: 'Accurate coding and proactive claim management to ensure you\'re paid for every service provided.' },
  { title: 'Tech-Driven Expertise', desc: 'Certified specialists using modern tools and analytics to maximize your financial performance.' },
  { title: 'Minimal Denials', desc: 'Errors are stopped at the source, ensuring claims are approved and paid the first time.' },
  { title: 'Faster Payments', desc: 'Streamlined digital workflows that accelerate reimbursements and improve daily cash flow.' },
  { title: 'Strategic Growth', desc: 'Smart data analysis to reduce overhead and uncover new revenue opportunities.' },
  { title: 'Patient-Focused Care', desc: 'We handle billing and paperwork so you can focus fully on patient care.' },
];

const valueTable = [
  { service: 'Claims Submission', result: 'High 1st-submission pass rates and reduced rejection.' },
  { service: 'Revenue Optimization', result: 'Strategic coding and billing to maximize your bottom line.' },
  { service: 'A/R Management', result: 'Faster recovery of aged receivables and improved cash flow.' },
  { service: 'Cost Reduction', result: 'Lower overhead by eliminating the need for in-house billing teams.' },
  { service: 'Credentialing', result: 'Seamless contracting so you can focus on patient care.' },
];

const services = [
  { title: 'Credentialing & Contracting', desc: 'Get enrolled with payers and negotiate better rates to maximize your practice\'s reach.' },
  { title: 'Patient Intake & Scheduling', desc: 'Capture accurate patient data from the start to prevent billing errors later.' },
  { title: 'Prior Authorization', desc: 'Secure approvals before treatment to guarantee every procedure is eligible for payment.' },
  { title: 'Medical Virtual Assistance', desc: 'Professional remote support to handle documentation and keep your office running smoothly.' },
  { title: 'Medical Billing', desc: 'High-precision coding and claim submission to ensure you get paid accurately and on time.' },
  { title: 'Claim Denial Management', desc: 'Targeted action to resolve rejections and recover revenue that others leave on the table.' },
  { title: 'Full RCM Services', desc: 'A comprehensive, end-to-end partnership that manages your entire financial lifecycle.' },
  { title: 'Quality Assurance Services', desc: 'Systematic audits to maintain HIPAA compliance and the highest billing standards.' },
];

const challenges = [
  { challenge: 'Stagnant Revenue', solution: 'Advanced Optimization – We identify hidden revenue leaks and deploy data-driven strategies to grow your bottom line.' },
  { challenge: 'High Denial Rates', solution: 'Precision Coding & Audits – Our certified team catches errors before they are submitted, ensuring a "clean claim" every time.' },
  { challenge: 'Slow Reimbursements', solution: 'Accelerated Workflows – We utilize digital-first processes to slash the time between patient care and cash in your bank.' },
  { challenge: 'Administrative Burden', solution: 'Complete RCM Management – We handle the heavy lifting of billing and paperwork so your staff can focus on patients.' },
];

const Home: React.FC = () => {
  return (
    <div className="home">

      {/* Hero */}
      <section className="hero">
        <div className="container">
          <div className="hero-text">
            <h1>
              Focus on <span className="highlight">Patients.</span><br />
              We'll Handle the <span className="highlight">Billing.</span>
            </h1>
            <p className="hero-subtitle">
              Transform your financial health with our specialized Revenue Cycle Management solutions.
            </p>
            <Link to="/contact" className="btn btn-cta">Connect Us</Link>
          </div>
          <div className="hero-image">
            <div className="hero-image-placeholder" aria-label="Doctor illustration" />
          </div>
        </div>
      </section>

      {/* Advantage */}
      <section className="advantage">
        <div className="container">
          <h2>The Nexus Prime Advantage</h2>
          <div className="advantage-grid">
            {advantages.map((a) => (
              <div className="advantage-card" key={a.stat}>
                <div className="advantage-stat">{a.stat}</div>
                <div className="advantage-desc">{a.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Elevate banner */}
      <section className="elevate-banner">
        <div className="container">
          <h2>Elevate Your Revenue. Optimize Your Future.</h2>
          <p>
            At Nexus Prime Group, we don't just manage billing — we engineer financial growth.
            As a premier Revenue Cycle Management partner, we deploy advanced, data-driven strategies
            designed to maximize your practice's bottom line while reducing operational stress.
          </p>
        </div>
      </section>

      {/* How We Transform */}
      <section className="transform">
        <div className="container">
          <h2>How We Transform Your Revenue Cycle</h2>
          <div className="transform-grid">
            {transformItems.map((item) => (
              <div className="transform-card" key={item.title}>
                <div className="transform-icon">✦</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Table */}
      <section className="value-section">
        <div className="container">
          <h2>Value You Can Count On</h2>
          <p className="value-sub">Partnering with Nexus Prime Group means more than just outsourcing — it means measurable growth.</p>
          <div className="value-table-wrap">
            <table className="value-table">
              <thead>
                <tr>
                  <th>The Service</th>
                  <th>The Result</th>
                </tr>
              </thead>
              <tbody>
                {valueTable.map((row) => (
                  <tr key={row.service}>
                    <td>{row.service}</td>
                    <td>{row.result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="services-section">
        <div className="container">
          <h2>Our Expertise & Services</h2>
          <div className="services-grid">
            {services.map((s) => (
              <div className="service-card" key={s.title}>
                <div className="service-icon">◆</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="challenges-section">
        <div className="container">
          <h2>Challenges You Face. Solutions We Deliver.</h2>
          <p className="challenges-sub">Managing a practice is demanding. We bridge the gap between administrative hurdles and financial success with targeted strategies.</p>
          <div className="challenges-table-wrap">
            <table className="challenges-table">
              <thead>
                <tr>
                  <th>The Challenge</th>
                  <th>The Nexus Solution</th>
                </tr>
              </thead>
              <tbody>
                {challenges.map((row) => (
                  <tr key={row.challenge}>
                    <td>{row.challenge}</td>
                    <td>{row.solution}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Success Formula */}
      <section className="formula-section">
        <div className="container">
          <h2>The Nexus Success Formula</h2>
          <div className="formula">
            <div className="formula-item">High-Accuracy Billing</div>
            <div className="formula-op">+</div>
            <div className="formula-item">Expert Support</div>
            <div className="formula-op">=</div>
            <div className="formula-result">Maximized Revenue</div>
          </div>
          <Link to="/contact" className="btn btn-cta">Get In Touch</Link>
        </div>
      </section>

    </div>
  );
};

export default Home;
